/**
 * <SEO> — declarative meta + JSON-LD per page.
 * Wraps react-helmet-async with sensible defaults and structured data builders.
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'AI Banter with Riddhi';
const SITE_URL = 'https://riddhi-responsibleai-corner.org';
const DEFAULT_DESCRIPTION =
  'AI ethics for teens, students, and curious minds — bite-sized lessons on responsible AI, machine learning, bias, and the latest models. Written by Riddhi Tikoo, a 10th-grade AI researcher.';
const DEFAULT_KEYWORDS = [
  'AI ethics for teens',
  'AI ethics for students',
  'learn AI ethics',
  'responsible AI',
  'AI bias explained',
  'AI safety for beginners',
  'high school AI lessons',
  'AI for high schoolers',
  'Claude Code tips',
  'ChatGPT prompting',
  'Riddhi Tikoo',
];
const DEFAULT_IMAGE = `${SITE_URL}/og-default.png`;
const TWITTER_HANDLE = '@riddhitikoo';

export const SEO = ({
  title,
  description,
  path = '/',
  image,
  type = 'website',
  publishedAt,
  updatedAt,
  author = 'Riddhi Tikoo',
  keywords,
  noIndex = false,
  jsonLd,
  children,
}) => {
  const fullTitle = title ? `${title} · ${SITE_NAME}` : SITE_NAME;
  const desc = description || DEFAULT_DESCRIPTION;
  const url = `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const ogImage = image || DEFAULT_IMAGE;
  const kw = (keywords && keywords.length ? keywords : DEFAULT_KEYWORDS).join(', ');

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={kw} />
      <meta name="author" content={author} />
      <link rel="canonical" href={url} />
      {noIndex ? <meta name="robots" content="noindex, nofollow" /> : <meta name="robots" content="index, follow, max-image-preview:large" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {publishedAt ? <meta property="article:published_time" content={publishedAt} /> : null}
      {updatedAt ? <meta property="article:modified_time" content={updatedAt} /> : null}
      {type === 'article' ? <meta property="article:author" content={author} /> : null}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured data */}
      {jsonLd ? (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      ) : null}

      {children}
    </Helmet>
  );
};

// ----- Structured-data builders -----

export const buildArticleJsonLd = ({ title, description, slug, image, author, publishedAt, updatedAt, keywords, category }) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: title,
  description,
  image: image ? [image] : [DEFAULT_IMAGE],
  author: {
    '@type': 'Person',
    name: author || 'Riddhi Tikoo',
    url: `${SITE_URL}/about`,
    sameAs: [
      'https://www.linkedin.com/in/riddhi-tikoo-32a02a2b7/',
      'https://github.com/riddhitik',
      'https://safeplayai.org',
    ],
  },
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}/logo.png`,
    },
  },
  datePublished: publishedAt,
  dateModified: updatedAt || publishedAt,
  mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/post/${slug}` },
  articleSection: category,
  keywords: (keywords || []).join(', '),
});

export const buildLessonJsonLd = ({ title, description, slug, topic, publishedAt, faqs }) => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'LearningResource',
      name: title,
      description,
      url: `${SITE_URL}/learn/${slug}`,
      learningResourceType: 'Lesson',
      educationalLevel: 'High school',
      teaches: topic,
      datePublished: publishedAt,
      author: {
        '@type': 'Person',
        name: 'Riddhi Tikoo',
        url: `${SITE_URL}/about`,
      },
    },
    ...(faqs && faqs.length
      ? [{
          '@type': 'FAQPage',
          mainEntity: faqs.map((q) => ({
            '@type': 'Question',
            name: q.question,
            acceptedAnswer: { '@type': 'Answer', text: q.answer },
          })),
        }]
      : []),
  ],
});

export const buildPersonJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: 'Riddhi Tikoo',
  url: `${SITE_URL}/about`,
  image: `${SITE_URL}/riddhi.jpg`,
  description:
    "10th-grade AI ethics researcher and student developer based in the Greater Seattle area. Founder of SafePlayAI. Building tools and lessons that make responsible AI accessible to high schoolers.",
  jobTitle: 'Student Researcher · Founder, SafePlayAI',
  alumniOf: 'Redmond High School',
  sameAs: [
    'https://www.linkedin.com/in/riddhi-tikoo-32a02a2b7/',
    'https://github.com/riddhitik',
    'https://safeplayai.org',
  ],
});

export const buildWebsiteJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  description: DEFAULT_DESCRIPTION,
  inLanguage: 'en-US',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/archive?search={query}`,
    'query-input': 'required name=query',
  },
  publisher: { '@type': 'Person', name: 'Riddhi Tikoo' },
});

export const buildBreadcrumbJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: it.url.startsWith('http') ? it.url : `${SITE_URL}${it.url}`,
  })),
});

export default SEO;
